import React from 'react';
import { Box, Menu, MenuItem } from '@mui/material';
import { useNavigate } from 'react-router';

import { getAnchorProps, MenuAnchor, saveTextContent, useBooleanStateWithToggle } from '@paragon/client-tools';
import { isLoggedInUserRoot } from '@paragon/login-tools';
import { enqueueError } from '@paragon/notification-tools';
import { runDelayedSpinner } from '@paragon/task-spinner';

import { AsyncThunkActionVoid } from '../../actions/thunkActionTypes';
import { useAppDispatch, useAppSelector } from '../hooks';
import { NameDialog } from './NameDialog';

interface Props {
  readonly noun: 'plate' | 'lumber' | 'hanger';
  readonly list: { readonly guid: string; readonly name: string; readonly isArchived: boolean };
  readonly anchor: MenuAnchor;
  readonly onClose: () => void;

  setDefaultListGuid: (guid: string) => AsyncThunkActionVoid;
  archiveList: (guid: string) => AsyncThunkActionVoid;
  restoreList: (guid: string) => AsyncThunkActionVoid;
  duplicateList: (guid: string) => AsyncThunkActionVoid;
  renameList: (guid: string, name: string) => AsyncThunkActionVoid;
}

export function ListMenu(props: Props) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const isUserRoot = useAppSelector(isLoggedInUserRoot);

  const [renameDialogOpen, toggleRenameDialogOpen] = useBooleanStateWithToggle();

  const anchorProps = getAnchorProps(props.anchor);

  const createMenuAction = (action: string, callback: () => Promise<void>) => () => {
    props.onClose();

    const spinnerMessage = `${action.charAt(0).toUpperCase() + action.slice(1)}...`;

    const cleanUpSpinner = dispatch(runDelayedSpinner(spinnerMessage));
    callback()
      .catch((error) => {
        console.error(error);
        dispatch(enqueueError(`Error while ${action}. Please contact Paragon Support.`));
      })
      .finally(() => {
        cleanUpSpinner();
      });
  };

  return (
    <Box>
      <Menu open={props.anchor != null} {...anchorProps} onClose={props.onClose}>
        <MenuItem
          onClick={createMenuAction(`setting default ${props.noun} list`, async () => {
            await dispatch(props.setDefaultListGuid(props.list.guid));
            navigate(`/`);
          })}
        >
          Make Default
        </MenuItem>
        <MenuItem
          onClick={createMenuAction(`renaming ${props.noun} list`, async () => {
            await dispatch(toggleRenameDialogOpen);
            navigate(`/`);
          })}
        >
          Rename
        </MenuItem>
        <MenuItem
          onClick={createMenuAction(`duplicating ${props.noun} list`, async () => {
            await dispatch(props.duplicateList(props.list.guid));
            navigate(`/`);
          })}
        >
          Duplicate
        </MenuItem>
        <MenuItem
          onClick={createMenuAction(`updating ${props.noun} list`, async () => {
            if (props.list.isArchived) {
              await dispatch(props.restoreList(props.list.guid));
            } else {
              await dispatch(props.archiveList(props.list.guid));
            }

            navigate(`/`);
          })}
        >
          {props.list.isArchived ? 'Restore' : 'Archive'}
        </MenuItem>
        {isUserRoot && (
          <MenuItem
            onClick={createMenuAction(`exporting ${props.noun} list`, async () => {
              const listWithNewGuid = { ...props.list, guid: crypto.randomUUID() };
              const fileContents = JSON.stringify(listWithNewGuid);
              saveTextContent(`${props.noun}list_${listWithNewGuid.name}.json`, fileContents);
              navigate(`/`);
            })}
          >
            Export
          </MenuItem>
        )}
      </Menu>
      <NameDialog
        verb="rename"
        noun={`${props.noun} list`}
        open={renameDialogOpen}
        defaultValue={props.list.name}
        onClose={toggleRenameDialogOpen}
        onSubmit={async (name) => {
          await dispatch(props.renameList(props.list.guid, name));
        }}
      />
    </Box>
  );
}
